// Field help for the finance screens (Overview, Revenue & margin, AWS costs, Reconciliation). Keys
// are the ids passed to <Field help="…">, card titles and column headers; every rendered label
// must have one (tests/unit/help.test.ts). Copy follows docs/guides/revenue-billing.md and
// docs/guides/admin-guide.md.
import type { HelpTopic } from "../topic";

export const FINANCE_TOPICS = {
  // ---- Overview ----
  "finance.billedThisMonth": {
    title: "Billed this month",
    body: "What billed customers owe for this month so far, at the rate plans in force: usage with its markup, plus fees and approved adjustments. The month is still open, so the figure grows until it is closed.",
    example: "On 25 September, $20,845.10 billed so far with a forecast of $25,014 means customers are on pace to be invoiced about $25,000 for September.",
    article: "revenue-and-margin",
    section: "forecast",
  },
  "finance.activeCustomers": {
    title: "Active customers",
    body: "Customers with at least one request this month, billed or not, out of every customer this screen knows about.",
    example: "11 of 12: one customer has sent no requests through the proxy since the 1st.",
    article: "revenue-and-margin",
  },
  "finance.tokensThisMonth": {
    title: "Tokens this month",
    body: "Every token metered this month for all customers: input, output and cache tokens together, billed or not.",
    example: "1.84 B tokens is 1,840,000,000 tokens across all customers since the 1st (UTC).",
    article: "revenue-and-margin",
  },

  // ---- Revenue & margin ----
  "finance.share": {
    title: "Share",
    body: "This row's part of the total billed in the table, so the biggest customers or models stand out.",
    example: "Hooli billed $26,068.88 of $124,137.54 in total, a 21 % share.",
    article: "revenue-and-margin",
    section: "reading-the-chart",
  },
  "finance.breakdown": {
    title: "Breakdown",
    body: "The same totals split by customer, by model or by month. The Months tab holds the exact numbers the chart draws. Plan fees, minimums and credits have their own row under Models, because they belong to no single model.",
    example: "Switch to Months to read March's billed $17,733.94, AWS cost $13,641.49 and profit $4,092.45 behind the March bar.",
    article: "revenue-and-margin",
    section: "reading-the-chart",
  },
  "finance.notBilled": {
    title: "Customers not billed",
    body: "Customers set to “not billed” for some of these months: they use the proxy but get no invoice. Their usage is left out of every total above. When AllCode pays their AWS bill, that cost is taken off the gross profit.",
    example: "Umbrella Test Co is not billed in August and AllCode pays its AWS bill of $412.08 at list price, so gross profit is $412.08 lower.",
    article: "revenue-and-margin",
    section: "not-billed",
  },
  "finance.awsPaidBy": {
    title: "AWS paid by",
    body: "Whose AWS bill the customer's Bedrock usage lands on. Only usage AllCode pays for is AllCode's cost; a customer paying AWS directly costs AllCode nothing here.",
    example: "A not-billed trial customer on AllCode's AWS account costs AllCode its usage; the same customer on its own AWS account costs AllCode nothing.",
    article: "revenue-and-margin",
    section: "not-billed",
  },
  "finance.allcodeCost": {
    title: "AllCode's AWS cost",
    body: "What serving this customer cost AllCode on the chosen cost basis. Empty when the customer pays AWS itself.",
    example: "At list price the customer's tokens cost $412.08; with the Actual AWS bill basis the Cost and Usage Report may say $395.60 after discounts.",
    article: "revenue-and-margin",
    section: "not-billed",
  },
  "finance.forecast": {
    title: "Forecast",
    body: "Where each billed customer's current month is heading: month to date, plus the run-rate for the days left, then the plan's minimum and fee. Late usage is not projected.",
    example: "On the 10th, $16.00 so far and $2.00 a day for 22 more days is $60.00 of usage; it clears a $50 minimum, and a $5 fee makes $65.00.",
    article: "revenue-and-margin",
    section: "forecast",
  },
  "finance.runRate": {
    title: "Run-rate a day",
    body: "The average billed amount a day over the last 7 whole UTC days. The forecast assumes the rest of the month continues at this rate.",
    example: "$14,000 billed over the last 7 days is a run-rate of $2,000.00 a day.",
    article: "revenue-and-margin",
    section: "forecast",
  },
  "finance.projected": {
    title: "Projected",
    body: "The customer's likely bill for the whole month: month to date plus run-rate × the days left, then the plan's minimum and fee.",
    example: "$800 so far on day 20 of 30 at $40 a day projects $1,200.00 for the month.",
    article: "revenue-and-margin",
    section: "forecast",
  },

  // ---- AWS costs ----
  "finance.fleetBasis": {
    title: "Fleet default cost basis",
    body: "The cost basis every customer follows unless it has its own. AWS list price prices tokens from the model prices. Actual AWS bill uses each customer's Cost and Usage Report (CUR); a customer following it needs a verified CUR source.",
    example: "Set Actual AWS bill from November 2026: from then on, profit for every customer that follows the default is measured against its CUR.",
    article: "aws-costs",
    section: "expense-basis",
  },
  "finance.effectiveFrom": {
    title: "Effective from",
    body: "The first month the new cost basis applies to. Earlier months keep the basis they had.",
    example: "Effective from Nov 2026 leaves October on list price and measures November onwards on the new basis.",
    article: "aws-costs",
    section: "expense-basis",
  },
  "finance.customerBasis": {
    title: "Customer's cost basis",
    body: "This customer's own cost basis by month. “Follow the fleet default” means it uses whatever the fleet default is in that month. Actual AWS bill needs a verified CUR source for this customer.",
    example: "Hooli on Actual AWS bill from Oct 2026, while the fleet default stays on AWS list price.",
    article: "aws-costs",
    section: "expense-basis",
  },
  "finance.curSources": {
    title: "CUR sources",
    body: "The Cost and Usage Report (CUR 2.0) exports Nexus reads for this customer's actual AWS bill. Each one belongs to a linked AWS account with the CUR capability. The worker verifies a new source, then reads it on a schedule.",
    example: "Add the export in bucket nexus-cur-<link id>-111122223333, prefix cur, export nexus-cur, region us-east-1, then Verify. It turns Active once the worker can read it.",
    article: "aws-costs",
    section: "cur-sources",
  },
  "finance.curAccount": {
    title: "AWS account",
    body: "The customer's linked AWS account the export lives in. Only accounts linked with the CUR capability can hold a CUR source, and each link has one.",
    example: "Account 111122223333, linked by the installer with the cur capability.",
    article: "aws-costs",
    section: "cur-sources",
  },
  "finance.curBucket": {
    title: "Bucket",
    body: "The S3 bucket the bootstrap stack made for the export, and no other: nexus-cur-<link id>-<account id>.",
    example: "nexus-cur-6f1c2a9e-…-111122223333 for account 111122223333.",
    article: "aws-costs",
    section: "cur-sources",
  },
  "finance.curPrefix": {
    title: "Prefix",
    body: "The folder in the bucket the export writes under. The bootstrap stack uses cur.",
    example: "cur: the export's files land under s3://<bucket>/cur/.",
    article: "aws-costs",
    section: "cur-sources",
  },
  "finance.curExportName": {
    title: "Export name",
    body: "The name of the CUR 2.0 data export in AWS Data Exports. The bootstrap stack names it nexus-cur.",
    example: "nexus-cur, the export the bootstrap stack created.",
    article: "aws-costs",
    section: "cur-sources",
  },
  "finance.curRegion": {
    title: "Region",
    body: "The AWS region of the export. AWS Data Exports exists only in us-east-1, so this is us-east-1.",
    example: "us-east-1, the only region AWS Data Exports runs in, whatever region the Bedrock usage is in.",
    article: "aws-costs",
    section: "cur-sources",
  },
  "finance.unmappedRows": {
    title: "Unmapped rows",
    body: "Rows with cost in the latest reads whose usage type has no verified mapping to a model and token class. Nexus never guesses them; they block finalizing a month until they are mapped (or the finalize is forced).",
    example: "3 unmapped rows of USE1-NewModel-input-tokens: add a verified usage-type mapping, then ingest again.",
    article: "aws-costs",
    section: "usage-types",
  },
  "finance.lastRun": {
    title: "Last successful read",
    body: "When the worker last read this export without an error. The worker reads each source on its schedule; Ingest now makes it due at the next tick.",
    example: "Yesterday 04:10: the export was read then and is up to date to that point.",
    article: "aws-costs",
    section: "cur-sources",
  },
  "finance.usageTypes": {
    title: "CUR usage-type map",
    body: "Translates a CUR usage type into a Bedrock model and token class, so the actual bill can be matched to metered tokens. It applies to every customer. Only verified mappings are used.",
    example: "AmazonBedrock, USE1-*-claude-sonnet-5-input-tokens → us.anthropic.claude-sonnet-5, Input.",
    article: "aws-costs",
    section: "usage-types",
  },
  "finance.productCode": {
    title: "Product code",
    body: "The CUR product code of the line items to map, usually AmazonBedrock.",
    example: "AmazonBedrock for every Bedrock model's token charges.",
    article: "aws-costs",
    section: "usage-types",
  },
  "finance.usageTypePattern": {
    title: "Usage type pattern",
    body: "The CUR line_item_usage_type to match. A leading region wildcard (*-) matches the same usage type in every region.",
    example: "*-claude-sonnet-5-input-tokens matches USE1-claude-sonnet-5-input-tokens and USW2-claude-sonnet-5-input-tokens.",
    article: "aws-costs",
    section: "usage-types",
  },
  "finance.bedrockModelId": {
    title: "Bedrock model id",
    body: "The Bedrock model the usage type belongs to, as the proxy meters it.",
    example: "us.anthropic.claude-sonnet-5 for the cross-region Sonnet profile.",
    article: "aws-costs",
    section: "usage-types",
  },
  "finance.tokenClass": {
    title: "Token class",
    body: "Which kind of token the usage type counts: input, output, cache read, or cache write (5-minute or 1-hour).",
    example: "…-input-tokens is Input; …-cache-read-input-tokens is Cache read.",
    article: "aws-costs",
    section: "usage-types",
  },
  "finance.mappingSource": {
    title: "Source",
    body: "Where the mapping comes from, so a reviewer can check it: an AWS pricing page, a CUR sample, a ticket.",
    example: "AWS Bedrock pricing page, 2026-09",
    article: "aws-costs",
    section: "usage-types",
  },
  "finance.markVerified": {
    title: "Mark verified",
    body: "Only verified mappings are used to read the actual bill. Marking one verified (or deleting a verified one) needs the billing:approve permission.",
    example: "A billing admin checks the pattern against a CUR sample, then adds it verified.",
    article: "aws-costs",
    section: "usage-types",
  },
  "finance.reconcileMonth": {
    title: "Reconcile and finalize a month",
    body: "Open one customer's month to compare the actual AWS bill with metered usage, then finalize it: finalizing freezes that month's AWS cost once the month is closed.",
    example: "Pick Hooli and August 2026, check nothing is pending or unmapped, then Finalize.",
    article: "aws-costs",
    section: "finalizing",
  },
  "finance.curNet": {
    title: "CUR net",
    body: "What AWS charged for this day, model and token class in the account, after discounts and before tax, from the Cost and Usage Report.",
    example: "$41.20 of Sonnet input tokens on 3 August in account 111122223333.",
    article: "aws-costs",
    section: "finalizing",
  },
  "finance.allocated": {
    title: "Allocated",
    body: "The part of the CUR cost matched to this customer's metered tokens.",
    example: "The CUR says $41.20 for 13.7 M tokens; Nexus metered all 13.7 M for this customer, so all $41.20 is allocated.",
    article: "aws-costs",
    section: "finalizing",
  },
  "finance.unattributed": {
    title: "Unattributed",
    body: "CUR cost that no metered token accounts for, such as Bedrock use in the account outside the proxy. It is shown, never charged to the customer. A negative figure means the proxy metered more than AWS billed.",
    example: "AWS billed $50.00 but the proxy metered usage worth $41.20: $8.80 is unattributed.",
    article: "aws-costs",
    section: "finalizing",
  },
  "finance.listCost": {
    title: "List cost",
    body: "The same metered tokens priced at AWS list price from the model prices, for comparison with the actual bill.",
    example: "13.7 M input tokens at $3.00 per million is a list cost of $41.10.",
    article: "aws-costs",
    section: "finalizing",
  },
  "finance.forceFinalize": {
    title: "Force",
    body: "Finalize despite a month that is not final in the CUR, unmapped rows, or a cost basis mismatch. Cells without an actual cost are priced at list cost. The force and your reason are recorded on the snapshot and in the audit log.",
    example: "AWS never marked the month final because the filtered export was empty; finance accepts list cost for it.",
    article: "aws-costs",
    section: "finalizing",
  },
  "finance.forceReason": {
    title: "Reason",
    body: "Why you are forcing it, 10 to 500 characters. It is stored with the snapshot and in the audit log.",
    example: "Empty CUR export for August; finance approved list cost (ticket FIN-212).",
    article: "aws-costs",
    section: "finalizing",
  },
  "finance.restate": {
    title: "Restate",
    body: "When AWS changes a month's Cost and Usage Report after it was finalized, restating appends a new revision; the next invoice carries the difference as late lines. The finalized snapshot and written invoices never change. Never restate during a rollout.",
    example: "AWS restated a cell from $1.000000 to $0.990001 after finalize: revision 2 is appended and the next invoice credits −$0.009999.",
    article: "aws-costs",
    section: "finalizing",
  },
  "finance.forceRestate": {
    title: "Force",
    body: "Release a restatement that is held because it would leave a cell of an actual-cost invoice without a cost. The next invoice credits those cells back. It needs a reason.",
    example: "The CUR is mid-rewrite and finance wants the revision now: force it with the ticket number as the reason.",
    article: "aws-costs",
    section: "finalizing",
  },
  "finance.snapshotDigest": {
    title: "Snapshot digest",
    body: "A SHA-256 of the frozen snapshot, so anyone can check later that the finalized figures were not changed.",
    example: "3f9a…c1: the same digest from the API later proves the snapshot is the one finalized.",
    article: "aws-costs",
    section: "finalizing",
  },

  // ---- Reconciliation ----
  "finance.drift": {
    title: "Drift",
    body: "How far the proxy's request count is from Bedrock's own meter (CloudWatch) for the day and model: (ours − Bedrock) ÷ Bedrock. Empty where Bedrock reported nothing or zero. Requests lean slightly negative, because Bedrock also counts requests the client hung up on.",
    example: "Ours 990 requests, Bedrock 1,000 invocations: drift −1.0%. Use in the account outside the proxy makes drift more negative.",
    article: "reconciliation",
    section: "drift",
  },
  "finance.driftStatus": {
    title: "Threshold",
    body: "Whether any figure of the cell drifts further than the alarm threshold (by default 1%). Over threshold is worth a look; findings say what drift cannot show.",
    example: "Drift −3.2% with a 1% threshold is Over threshold.",
    article: "reconciliation",
    section: "drift",
  },
  "finance.findings": {
    title: "Findings",
    body: "What this cell's drift cannot show: Bedrock reported nothing, AWS publishes no cache metric for the model, or another installation shares the AWS account.",
    example: "“Another installation shares this AWS account”: both installations see the same account-wide figures, so each is compared on its own.",
    article: "reconciliation",
    section: "drift",
  },
  "finance.bedrockDrift": {
    title: "Bedrock drift",
    body: "Compares the proxy's metered usage with Bedrock's own CloudWatch meter, per installation, day and model. CloudWatch counts the whole AWS account, so use outside the proxy shows up as drift.",
    example: "A day with drift near 0% means the ledger and Bedrock agree; −40% usually means someone used Bedrock in the account directly.",
    article: "reconciliation",
    section: "drift",
  },
  "finance.completeness": {
    title: "Metering completeness",
    body: "Whether every request the proxy served reached the ledger, per installation: requests served (from heartbeat counters), events ingested, spilled and dropped.",
    example: "Served 10,000, ingested 9,990: completeness 99.9%. Any dropped event is lost billing data.",
    article: "reconciliation",
    section: "completeness",
  },
  "finance.completenessPct": {
    title: "Completeness",
    body: "Events ingested ÷ requests served. Empty when the installation sent no counters or served nothing.",
    example: "9,990 ingested of 10,000 served is 99.9%.",
    article: "reconciliation",
    section: "completeness",
  },
  "finance.spilled": {
    title: "Spilled",
    body: "Events the proxy could not put on the events queue and wrote to its spill bucket instead. They are drained back onto the queue every minute, so spilled is not lost.",
    example: "120 spilled during a queue outage, all ingested a few minutes later.",
    article: "reconciliation",
    section: "completeness",
  },
  "finance.dropped": {
    title: "Dropped",
    body: "Events the proxy could neither queue nor spill: lost billing data. Anything above zero needs a look.",
    example: "2 dropped while both the events queue and the spill bucket were unreachable.",
    article: "reconciliation",
    section: "completeness",
  },
} satisfies Record<string, HelpTopic>;
